import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import { createEbook, deleteEbook, getEbookById, getEbooks, updateEbook } from '../../api/admin/adminEbooks';
import { useNavigate } from 'react-router-dom';


export const useEbooks = () => {
  const { setError } = useAuthStore();
  
  const {
    data: ebooks,
    isLoading,
    error,
    refetch
  } = useQuery('ebooks', getEbooks, {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch ebooks');
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    ebooks,
    isLoading,
    error,
    refetchEbooks: refetch
  };
};

export const useEbook = (id) => {
  const { setError } = useAuthStore();
  
  const {
    data: ebook,
    isLoading,
    error
  } = useQuery(['ebook', id], () => getEbookById(id), {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch ebook');
    },
    retry: false,
    enabled: !!id,
  });

  return {
    ebook,
    isLoading,
    error
  };
};

export const useCreateEbook = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();
  const navigate=useNavigate()

  const mutation = useMutation(createEbook, {
    onSuccess: () => {
      toast.success('Ebook created successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('ebooks');
      navigate("/admin/dashboard", {
        state: { data: "media" },
      });
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create ebook';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    createEbook: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useUpdateEbook = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(updateEbook, {
    onSuccess: (data, variables) => {
      toast.success('Ebook updated successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('ebooks');
      queryClient.invalidateQueries(['ebook', variables.id]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update ebook';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    updateEbook: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useDeleteEbook = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(deleteEbook, {
    onSuccess: () => {
      toast.success('Ebook deleted successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('ebooks');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete ebook';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    deleteEbook: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error
  };
};